import {List, Map} from 'immutable'
import {useMemo, memo} from 'react'
import PropTypes from 'prop-types'

import SearchFormContainer from './SearchFormContainer'

import {responsesWithItemResponse} from '../../../../../lib/plan_data/itemResponsesHelper'
import {itemsWithElementAndCategoryNames} from '../../../../../lib/plan_data/item'
import searchConfig from './searchConfig'


export const Search = ({responsesWithItem = List(), ...props}) => {
  const itemsElementAndCategoryNames = useMemo(
    () => itemsWithElementAndCategoryNames({items: props.eligibleItemsForSearch, kits: props.kits, listMappings: props.listMappings, categoryDictionary: props.categoryDictionary}),
    [props.eligibleItemsForSearch.size]
  )

  const searchableTextByResponseId = useMemo(() => Map(responsesWithItem.map(response => [response.get('id'), response.get('display-text')])), [responsesWithItem])

  const responsesElementAndItem = useMemo(() => responsesWithItemResponse({itemResponses: props.itemResponses, elementViewMap: props.elementViewMap, searchableTextByResponseId}), [props.itemResponses, searchableTextByResponseId])

  // Prepare vault extension data for search
  const vaultExtensionSearchData = useMemo(() => {
    if (!props.vaultExtension || props.vaultExtension.isEmpty()) return List()

    // Extract cart item names and their sub-item names
    const cartItems = props.vaultExtension.getIn(['data', 'plan', 'cart-items']) || List()

    // Find the package name by looking for the cart item with category "Package"
    const packageCartItem = cartItems.find(item =>
      item.getIn(['line-item', 'category']) === 'Package'
    )
    const packageName = packageCartItem?.getIn(['line-item', 'name']) || ''

    const cartItemNames = cartItems
      .flatMap(item => {
        const mainItemName = item.getIn(['line-item', 'name'])
        const subItemNames = (item.get('sub-items') || List())
          .map(subItem => subItem.getIn(['line-item', 'name']))
          .filter(name => name)

        return List([mainItemName]).concat(subItemNames).filter(name => name)
      })
      .join(' ')

    const customCartItemNames = (props.vaultExtension.getIn(['data', 'plan', 'custom-cart-items']) || List())
      .map(item => item.get('name'))
      .filter(name => name)
      .join(' ')

    const afpFirstName = props.vaultExtension.getIn(['data', 'afp', 'first-name']) || ''
    const afpLastName = props.vaultExtension.getIn(['data', 'afp', 'last-name']) || ''
    const afpName = [afpFirstName, afpLastName].filter(name => name).join(' ')

    // Add additional keywords for better searchability
    const keywords = 'policy out of area protection policy number funeral plan package details my prearrangements prearranged funeral burial cremation memorial'

    return List([{
      id: props.vaultExtension.get('id'),
      'package-name': packageName,
      'cart-items': cartItemNames,
      'custom-cart-items': customCartItemNames,
      'afp-name': afpName,
      'afp-email': props.vaultExtension.getIn(['data', 'afp', 'email']) || '',
      'afp-phone': props.vaultExtension.getIn(['data', 'afp', 'phone']) || '',
      'firm-name': props.firmName,
      keywords
    }])
  }, [props.vaultExtension])

  const responseSearch = searchConfig({
    indices: ['searchable-value', 'display-name', ['itemResponse', 'header']],
    documents: responsesElementAndItem.toJS()
  })

  const itemSearch = searchConfig({
    indices: ['header', 'elementNamesAndListMappingValues', 'categoryName'],
    documents: itemsElementAndCategoryNames.toJS()
  })

  const contactSearch = searchConfig({
    indices: ['first-name', 'last-name', 'company-name', 'emails', 'phone-numbers'],
    documents: props.newContacts.toJS()
  })

  const vaultExtensionSearch = searchConfig({
    indices: ['package-name', 'cart-items', 'custom-cart-items', 'afp-name', 'afp-email', 'afp-phone', 'keywords', 'firm-name'],
    documents: vaultExtensionSearchData.toJS()
  })

  return (
    <SearchFormContainer
      {...props}
      responseSearch={responseSearch}
      itemSearch={itemSearch}
      contactSearch={contactSearch}
      vaultExtensionSearch={vaultExtensionSearch}
      responses={responsesElementAndItem}
    />
  )
}

Search.propTypes = {
  elementViewMap: PropTypes.instanceOf(Map),
  items: PropTypes.instanceOf(List),
  newContacts: PropTypes.instanceOf(List),
  kits: PropTypes.instanceOf(Map),
  listMappings: PropTypes.instanceOf(Map),
  categoryDictionary: PropTypes.instanceOf(Map),
  itemResponses: PropTypes.instanceOf(List),
  responsesWithItem: PropTypes.instanceOf(List),
  eligibleItemsForSearch: PropTypes.instanceOf(List),
  vaultExtension: PropTypes.instanceOf(Map),
  firmName: PropTypes.string
}

const areEqual = (prevProps, nextProps) => (
  prevProps.eligibleItemsForSearch.equals(nextProps.eligibleItemsForSearch) &&
  prevProps.itemResponses.equals(nextProps.itemResponses) &&
  prevProps.responsesWithItem.equals(nextProps.responsesWithItem) &&
  prevProps.newContacts.equals(nextProps.newContacts) &&
  (prevProps.vaultExtension || Map()).equals(nextProps.vaultExtension || Map())
)

export default memo(Search, areEqual)
